// JustEasySEO — Card + KpiCard + ProposalCard + PageHeader + StatusBadge +
// EmptyState + LoadingState + ErrorState. Alles op design-tokens, geen
// hardcoded hex.
import React from "react";
import { Loader2, AlertCircle, Inbox, CheckCircle2, Info, Link as LinkIcon } from "lucide-react";
import { Button } from "./Button";

export function Card({ children, className = "", padding = "md", header, footer,
                       ...rest }) {
  const pad = padding === "none" ? "" :
              padding === "sm" ? "p-4" :
              padding === "lg" ? "p-8" : "p-6";
  return (
    <div
      className={`bg-bg-panel rounded-card border border-border-hairline shadow-card ${className}`}
      {...rest}
    >
      {header && (
        <div className="px-6 py-4 border-b border-border-hairline">
          {header}
        </div>
      )}
      <div className={pad}>{children}</div>
      {footer && (
        <div className="px-6 py-3 border-t border-border-hairline bg-bg-panel-raised rounded-b-card">
          {footer}
        </div>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, action, className = "" }) {
  return (
    <div className={`mb-6 flex items-start justify-between gap-4 flex-wrap ${className}`}>
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-text-primary tracking-tight"
             data-testid="page-header-title">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-text-muted mt-1 max-w-2xl"
              data-testid="page-header-subtitle">
            {subtitle}
          </p>
        )}
      </div>
      {action && <div className="shrink-0" data-testid="page-header-action">{action}</div>}
    </div>
  );
}

export function KpiCard({ icon: Icon, label, value, subline,
                          source, period, lastSync, tone = "neutral",
                          testid, empty = false, emptyHint }) {
  const toneRing = {
    neutral: "bg-accent-mint text-accent-teal",
    positive: "bg-[#DDF0E4] text-pos-green",
    warning: "bg-[#FBE9CE] text-warn-amber",
    negative: "bg-[#F4DADA] text-neg-red",
  }[tone];
  return (
    <div className="bg-bg-panel rounded-card border border-border-hairline p-5 shadow-card flex flex-col gap-3"
          data-testid={testid || "kpi-card"}>
      <div className="flex items-center gap-3">
        {Icon && (
          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${toneRing}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
        <div className="text-sm text-text-muted">{label}</div>
      </div>
      {empty ? (
        <div>
          <div className="text-2xl font-semibold text-text-subtle">—</div>
          {emptyHint && (
            <div className="text-xs text-text-muted mt-1">{emptyHint}</div>
          )}
        </div>
      ) : (
        <div>
          <div className="text-2xl md:text-3xl font-semibold text-text-primary tabular-nums">
            {value}
          </div>
          {subline && (
            <div className="text-xs text-text-muted mt-1">{subline}</div>
          )}
        </div>
      )}
      {(source || period || lastSync) && (
        <div className="text-xs text-text-subtle pt-2 border-t border-border-subtle flex flex-wrap gap-x-3 gap-y-1">
          {source && <span>Bron: {source}</span>}
          {period && <span>Periode: {period}</span>}
          {lastSync && <span>Laatst bijgewerkt: {lastSync}</span>}
        </div>
      )}
    </div>
  );
}

export function ProposalCard({ title, what, why, basis, kosten, volgendeStap,
                                onOpen, ghostNote = true, testid }) {
  return (
    <div className="bg-bg-panel rounded-card border border-border-hairline shadow-card p-6 space-y-4"
          data-testid={testid || "proposal-card"}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-accent-mint flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-4 h-4 text-accent-teal" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-text-primary leading-snug">{title}</h3>
          {ghostNote && (
            <p className="text-xs text-text-muted mt-1">
              We analyseren en adviseren. In Ghost Mode wijzigen of publiceren we niets.
            </p>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 text-sm">
        {what && (<div><div className="text-xs text-text-muted mb-1">Wat</div>
                    <div className="text-text-primary">{what}</div></div>)}
        {why && (<div><div className="text-xs text-text-muted mb-1">Waarom</div>
                   <div className="text-text-primary">{why}</div></div>)}
        {basis && (<div><div className="text-xs text-text-muted mb-1">Basis</div>
                     <div className="text-text-primary">{basis}</div></div>)}
        {kosten && (<div><div className="text-xs text-text-muted mb-1">Kosten</div>
                      <div className="text-text-primary">{kosten}</div></div>)}
      </div>
      {volgendeStap && (
        <div className="rounded-md bg-accent-mint text-text-primary p-3 text-sm">
          <span className="font-medium">Volgende stap:</span> {volgendeStap}
        </div>
      )}
      {onOpen && (
        <div className="pt-1">
          <Button onClick={onOpen} variant="primary" size="sm"
                   data-testid={`${testid || "proposal-card"}-open`}>
            Bekijk voorstel
          </Button>
        </div>
      )}
    </div>
  );
}

const STATUS_MAP = {
  connected: { label: "Gekoppeld", bg: "bg-[#DDF0E4]", text: "text-pos-green", Icon: CheckCircle2 },
  disconnected: { label: "Niet gekoppeld", bg: "bg-bg-panel-raised", text: "text-text-muted", Icon: LinkIcon },
  connecting: { label: "Verbinden…", bg: "bg-accent-mint", text: "text-accent-teal", Icon: Loader2 },
  reauth: { label: "Opnieuw aanmelden", bg: "bg-[#FBE9CE]", text: "text-warn-amber", Icon: AlertCircle },
  error: { label: "Fout", bg: "bg-[#F4DADA]", text: "text-neg-red", Icon: AlertCircle },
  waiting: { label: "Wacht op activatie", bg: "bg-[#FBE9CE]", text: "text-warn-amber", Icon: Info },
};

export function StatusBadge({ status = "disconnected", label, testid }) {
  const c = STATUS_MAP[status] || STATUS_MAP.disconnected;
  const Icon = c.Icon;
  const spin = status === "connecting" ? "animate-spin" : "";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill text-xs font-medium ${c.bg} ${c.text}`}
           data-testid={testid || `status-${status}`}>
      <Icon className={`w-3 h-3 ${spin}`} />
      {label || c.label}
    </span>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, body, action, testid }) {
  return (
    <div className="bg-bg-panel rounded-card border border-dashed border-border-hairline p-10 text-center"
          data-testid={testid || "empty-state"}>
      <div className="w-12 h-12 rounded-full bg-accent-mint flex items-center justify-center mx-auto mb-4">
        <Icon className="w-5 h-5 text-accent-teal" />
      </div>
      {title && <h3 className="text-lg font-semibold text-text-primary mb-2">{title}</h3>}
      {body && <p className="text-sm text-text-muted max-w-md mx-auto mb-4 leading-relaxed">{body}</p>}
      {action}
    </div>
  );
}

export function LoadingState({ label = "Bezig met laden…", testid }) {
  return (
    <div className="flex items-center gap-2 text-text-muted text-sm py-4"
          data-testid={testid || "loading-state"}>
      <Loader2 className="w-4 h-4 animate-spin" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorState({ title = "Er ging iets mis", body, onRetry, testid }) {
  return (
    <div className="rounded-card border border-neg-red/30 bg-[#F4DADA]/40 p-5 flex items-start gap-3"
          data-testid={testid || "error-state"}>
      <AlertCircle className="w-5 h-5 text-neg-red mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="font-medium text-text-primary">{title}</div>
        {body && <div className="text-sm text-text-muted mt-1">{body}</div>}
        {onRetry && (
          <div className="mt-3">
            <Button onClick={onRetry} variant="secondary" size="sm">
              Probeer opnieuw
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
